import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth, useAuthenticatedFetch } from './AuthContext';
import { usePlan } from './PlanContext';
import type { Client } from '../types';
import { CLIENT_LIMITS } from '../types';

interface ClientContextValue {
  clients: Client[];
  activeClientId: string | null; // null = personal/all, 'personal' = personal only, uuid = specific client
  isLoading: boolean;
  error: string | null;
  clientLimit: number;
  canAddClient: boolean;
  // Actions
  setActiveClient: (clientId: string | null) => void;
  createClient: (data: Omit<Client, 'id' | 'managerId' | 'createdAt' | 'updatedAt'>) => Promise<Client | null>;
  updateClient: (id: string, data: Partial<Client>) => Promise<boolean>;
  deleteClient: (id: string) => Promise<boolean>;
  refreshClients: () => Promise<void>;
}

const ClientContext = createContext<ClientContextValue | undefined>(undefined);

// Helper to convert API client to frontend Client
function parseClient(apiClient: Record<string, unknown>): Client {
  return {
    id: apiClient.id as string,
    managerId: apiClient.manager_id as string,
    name: apiClient.name as string,
    email: apiClient.email as string | undefined,
    avatarUrl: apiClient.avatar_url as string | undefined,
    brandColor: apiClient.brand_color as string | undefined,
    notes: apiClient.notes as string | undefined,
    postCount: apiClient.postCount as number | undefined,
    createdAt: new Date(apiClient.created_at as string),
    updatedAt: new Date(apiClient.updated_at as string),
  };
}

const ACTIVE_CLIENT_KEY = 'doopl_active_client';

export function ClientProvider({ children }: { children: ReactNode }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [activeClientId, setActiveClientIdState] = useState<string | null>(() => {
    return localStorage.getItem(ACTIVE_CLIENT_KEY);
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { isAuthenticated, token } = useAuth();
  const { subscription } = usePlan();
  const authFetch = useAuthenticatedFetch();

  const clientLimit = CLIENT_LIMITS[subscription.planId] || CLIENT_LIMITS.free;
  const canAddClient = clients.length < clientLimit;

  // Load clients
  const refreshClients = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setClients([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch('/clients').catch(() => null);

      if (response?.ok) {
        const data = await response.json();
        setClients((data.clients || []).map(parseClient));
      } else {
        setClients([]);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, token, authFetch]);

  useEffect(() => {
    refreshClients();
  }, [refreshClients]);

  // Set active client with persistence
  const setActiveClient = useCallback((clientId: string | null) => {
    setActiveClientIdState(clientId);
    if (clientId) {
      localStorage.setItem(ACTIVE_CLIENT_KEY, clientId);
    } else {
      localStorage.removeItem(ACTIVE_CLIENT_KEY);
    }
  }, []);

  // Validate active client exists
  useEffect(() => {
    if (activeClientId && activeClientId !== 'personal' && clients.length > 0) {
      const clientExists = clients.some(c => c.id === activeClientId);
      if (!clientExists) {
        setActiveClient(null);
      }
    }
  }, [activeClientId, clients, setActiveClient]);

  // Create client
  const createClient = useCallback(async (
    data: Omit<Client, 'id' | 'managerId' | 'createdAt' | 'updatedAt'>
  ): Promise<Client | null> => {
    if (!canAddClient) {
      setError(`Client limit reached (${clientLimit}). Upgrade your plan for more clients.`);
      return null;
    }

    try {
      const response = await authFetch('/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          avatarUrl: data.avatarUrl,
          brandColor: data.brandColor,
          notes: data.notes,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newClient = parseClient(result.client);
        setClients(prev => [...prev, newClient]);
        return newClient;
      }

      const errData = await response.json();
      setError(errData.error || 'Failed to create client');
      return null;
    } catch (err) {
      console.error('Failed to create client:', err);
      setError('Network error creating client');
      return null;
    }
  }, [authFetch, canAddClient, clientLimit]);

  // Update client
  const updateClient = useCallback(async (id: string, data: Partial<Client>): Promise<boolean> => {
    try {
      const response = await authFetch(`/clients/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          avatarUrl: data.avatarUrl,
          brandColor: data.brandColor,
          notes: data.notes,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const updatedClient = parseClient(result.client);
        setClients(prev => prev.map(c => c.id === id ? updatedClient : c));
        return true;
      }

      return false;
    } catch (err) {
      console.error('Failed to update client:', err);
      return false;
    }
  }, [authFetch]);

  // Delete client
  const deleteClient = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await authFetch(`/clients/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setClients(prev => prev.filter(c => c.id !== id));
        // If deleted client was active, reset to null
        if (activeClientId === id) {
          setActiveClient(null);
        }
        return true;
      }

      return false;
    } catch (err) {
      console.error('Failed to delete client:', err);
      return false;
    }
  }, [authFetch, activeClientId, setActiveClient]);

  return (
    <ClientContext.Provider
      value={{
        clients,
        activeClientId,
        isLoading,
        error,
        clientLimit,
        canAddClient,
        setActiveClient,
        createClient,
        updateClient,
        deleteClient,
        refreshClients,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

// Default value for when context isn't available yet (during initial render)
const DEFAULT_CLIENT_VALUE: ClientContextValue = {
  clients: [],
  activeClientId: null,
  isLoading: true,
  error: null,
  clientLimit: 3,
  canAddClient: false,
  setActiveClient: () => {},
  createClient: async () => null,
  updateClient: async () => false,
  deleteClient: async () => false,
  refreshClients: async () => {},
};

export function useClients() {
  const context = useContext(ClientContext);
  // Return default value if context not ready (prevents crashes during initial render)
  return context || DEFAULT_CLIENT_VALUE;
}
